/** /itineraries/:id/:tab? — the itinerary record (plan §1, §2, §5, §6, §7 provenance, §8, §9.8,
 *  §10.3; O1, O4, O5, O9, O10; D3, D6, D8): a catalog entry guests open in the app and tours copy,
 *  read first and edited in place on the records core — the activity record's sibling.
 *
 *  - **Header** — `ItineraryRecordHeader`: the name the app shows, the status chip (Live /
 *    Coming soon / Not published / Placeholder) and the source chip, the identity subtitle; the
 *    Actions menu and **Edit** (`E`) / **Close editor** (`Esc`). No console_itineraries_manage →
 *    no Edit; the `ConsoleCatalogEditEnabled` switch off → Edit disabled, "Editing is switched
 *    off"; an itinerary without an operator → Edit disabled, "No owning operator"; an editor
 *    that could not load → Edit disabled with that reason, and a banner with Retry. There is no
 *    Published switch (O1): in view mode Actions › Visibility offers Publish now / Unpublish
 *    now — one step, confirmed with its consequences, ending in a receipt with the audit row
 *    (`usePublishNow`), disabled while the editor keeps unsaved changes ("Save or discard your
 *    N unsaved changes first"); in edit mode `published` is a draft field (Visibility & search)
 *    and the only control.
 *  - **Tabs** (URL, keeping `?edit`): Itinerary (the record) · Images N · Usage · History
 *    (console_audit_view). Old slugs redirect: `general` → the record, `days` → the record's
 *    Days card (`#days`), `used-by` → `usage`. Images is the catalog itinerary's gallery;
 *    without a catalog itinerary it asks for none and says what creates one (O9), with no
 *    count on the tab. Usage is the full, paged used-by — tours, other
 *    itineraries, trips added, every row blocking Delete — with the live tours' upcoming
 *    departures from the used-by summary (`ItineraryUsageTab`). History merges the catalog
 *    itinerary's root (`also=demo.itinerary:<id>`, O6) and names each row's record.
 *  - **Edge states** (O9) — banners under the header on every tab, beside the orphan banner: a
 *    placeholder for new tours (`name` / `published` locked by its guard, no Delete), no
 *    catalog itinerary (a save creates it), a catalog itinerary shared with other itineraries.
 *  - **Fact band** (record tab) — In the app · Trip · App button · Used by · Source; the used-by
 *    summary is asked for only once the band has been on screen — never with the detail.
 *  - **Cards** (keys `1`–`7`) — FieldSlots over the registry, the values from the draft's
 *    baselines (edit_context's, else the detail's): Catalog entry, App button, Days (the Day
 *    ribbon over the days' outline, one day open in place — `DaysCard`), Content, Visibility &
 *    search, Access, Identity & source.
 *  - **Edit mode** — exactly the record editor: `?edit=1` (or `E`) once the edit catalog and
 *    edit_context are here; the draft (`DraftProvider kind="itinerary"`, seeded from
 *    edit_context `baselines`, the day list merged on a refetch) and the editing session are
 *    PAGE-level, so a tab switch keeps both and only leaving the itinerary is guarded.
 *    FieldSlots swap in their inputs in place (Zero-Re-Layout); fields a feed may overwrite
 *    carry its verdict (D4). The route map (and a day's image) upload at once and the draft
 *    holds the Image id (D3). The preview plans the draft (the ledger's lines, the app caches,
 *    the app reach — D8 —, a missing catalog itinerary created, a shared one's other
 *    itineraries); Save is the apply, compare-and-set per field: a 409 turns the drifted fields
 *    stale (Keep mine / Take theirs) or "deleted elsewhere", a 400 files its errors under their
 *    fields, a save ends in the receipt with the audit link and re-seeds the page.
 *  - **The dock** (O5) — from 1100 px of content a 380 px column beside the panels on every tab
 *    but History, in both modes: **Preview | Changes N** (the phone renders the draft's
 *    changes — O4 — on request). History docks the plain ledger in edit mode only. Below 1100
 *    px the ledger is the bottom sheet and the header's Preview opens the phone in a drawer.
 *  - **States** — the page's shape while loading (the band, the cards in their rows, the
 *    dock's column; one panel and the dock on Images and Usage; one panel on History); the
 *    catalog records' empty states for a missing itinerary (404) or one the operator may not
 *    see (403); a load error with Retry; a failed refresh keeps the loaded copy under a notice;
 *    a draft restored from sessionStorage (or kept after leaving edit mode) is announced in
 *    view mode; an import of the itinerary's source running now is announced.
 *  - **Delete** — Actions → Danger → Delete…, never on a placeholder; disabled up front while
 *    what the detail (and, once read, the summary) counts holds it, with the reason and Open
 *    Usage beneath it. Otherwise it confirms first (naming the unsaved changes that go with
 *    it); the server stays the authority — a refused delete (`409 itinerary_in_use`) opens the
 *    refusal dialog listing what depends on it, and offers Unpublish (Unpublish now's
 *    confirmation) while it is published. */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SyncIcon from '@mui/icons-material/Sync';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Navigate, Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as itinerariesApi from '@/api/itineraries';
import type { ItineraryDetail } from '@/api/itineraries';
import {
  cachedItineraryDayCount,
  ITINERARY_RECORD_SECTIONS,
  toItineraryChangeset,
  useItineraryDetail,
  useItineraryEditCatalog,
  useItineraryEditContext,
  useItineraryUsedBySummary,
  type ItineraryBaselines,
  type ItineraryRecordEditContext,
} from '@/api/itineraryRecord';
import { makeGalleryApi } from '@/api/media';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel, type HistoryAlsoRoot } from '@/components/History/HistoryPanel';
import { ImageGallery } from '@/components/ImageGallery';
import type { PreviewApplied } from '@/components/MobilePreview';
import { useSnackbar } from '@/components/Snackbar';
import {
  CatalogHeaderSkeleton,
  CatalogLoadError,
  CatalogRefreshError,
  ITINERARY_RECORD,
} from '@/features/catalog/CatalogLoadStates';
import { CATALOG_EDITING_OFF, CATALOG_EDITOR_LOAD_FAILED, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { FactBandSkeleton } from '@/features/records/band/FactBand';
import { parseRecordKey, type RecordChangeset, type RecordKey } from '@/features/records/draft/changeSet';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import type { RecordActionGroup, RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { RecordEmptyState, RecordPageSkeleton } from '@/features/records/page/RecordStates';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  loadsRecordTab,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';
import { PREVIEW_DOCK_WIDTH, PreviewDock, PreviewDockSkeleton } from '@/features/records/preview/PreviewDock';
import { PreviewDrawer } from '@/features/records/preview/PreviewDrawer';
import { RecordPreviewPane } from '@/features/records/preview/RecordPreviewPane';
import { RecordPreviewProvider } from '@/features/records/preview/RecordPreviewProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';
import { cond, extIdsHref, itinerariesHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { itineraryDeleteRefusal, type ItineraryDeleteRefusal } from '../deleteRefusal';
import { ItineraryDeleteRefusalDialog } from '../ItineraryDeleteRefusalDialog';

import { unsavedFirst, usePublishNow } from './actions/publishNow';
import { useRegenerateDeepLink } from './actions/regenerateDeepLink';
import { useCopyDeepLink, type ItineraryDisplayContext } from './cards/displays';
import { itineraryCardRows, itineraryCardSkeleton, ItineraryCardsView, itineraryColumns } from './cards/ItineraryCardsView';
import { itinerarySectionAnchorId } from './cards/layout';
import { DaysChannel } from './days/daysChannel';
import { ITINERARY_FRESH } from './days/daysModel';
import {
  ITINERARY_MANAGE_PERM,
  ITINERARY_TIMEZONE,
  itineraryEditBlockedReason,
  useItineraryEditorConfig,
} from './editorConfig';
import { ItineraryFactBand, ItineraryNotices } from './parts/ItineraryFactBand';
import {
  itineraryDeleteBlockedReason,
  noCatalogItineraryImagesText,
  type ItineraryCardKey,
  type ItineraryRecordTab,
} from './parts/itineraryFactBandModel';
import { ITINERARY_ACTION, ItineraryRecordHeader, itineraryTitle } from './parts/ItineraryRecordHeader';
import { ItineraryUsageTab } from './parts/ItineraryUsageTab';
import { ITINERARY_DAY_ENTITY } from './registry';
import { itineraryBaselines } from './viewConfig';

const MANAGE_PERM = ITINERARY_MANAGE_PERM;

/** Old tab slugs (bookmarks, links from other pages) → the tab that took them over, and the
 *  card on it (`#days`: the old Days tab is the record's Days card). */
export const ITINERARY_SLUG_REDIRECTS: Readonly<Record<string, { tab: ItineraryRecordTab; hash?: string }>> = {
  general: { tab: 'itinerary' },
  days: { tab: 'itinerary', hash: 'days' },
  'used-by': { tab: 'usage' },
};

/** The URL fragments that name a card of the record (the old Days tab's redirect). */
const HASH_CARDS: Readonly<Record<string, ItineraryCardKey>> = { '#days': 'days' };

const SECTION_LABEL: Readonly<Record<ItineraryRecordTab, string>> = {
  itinerary: 'Itinerary',
  images: 'Images',
  usage: 'Usage',
  history: 'History',
};

const TABS_LABELS = { label: 'Itinerary sections', refreshingLabel: 'Refreshing the itinerary' };

/** What refuses an itinerary's delete, in the refusal's words. */
export const DELETE_REFUSED_BY = 'tours, travellers’ trips or other itineraries';

/** The delete's confirmation: the itinerary by the name the app shows, what refuses it, and
 *  the draft's unsaved changes (they go too). */
export function itineraryDeleteMessage(title: string, unsaved = 0): string {
  const draft =
    unsaved === 1 ? ' Your unsaved change goes with it.' : unsaved > 1 ? ` Your ${unsaved} unsaved changes go with it.` : '';
  return (
    `“${title}” will be deleted.${draft} This cannot be undone. ` +
    `The delete is refused while ${DELETE_REFUSED_BY} depend on it.`
  );
}

/** History's two roots (O6): the itinerary group's own rows, and its catalog itinerary's. */
export const HISTORY_GROUP_LABEL = 'Itinerary';
export const HISTORY_DEFAULT_LABEL = 'Catalog itinerary';
/** History's 403: it needs both permissions with the merged root (A11). */
export const HISTORY_FORBIDDEN_TEXT =
  'You can’t see this history: it needs the Audit view and Itineraries view permissions ' +
  '(console_audit_view, console_itineraries_view).';

/** The catalog (default) itinerary merged into History (`also=demo.itinerary:<id>`, O6 / A11);
 *  null without one (the group's history alone). */
export function itineraryHistoryAlso(d: Pick<ItineraryDetail, 'default_itinerary'>): HistoryAlsoRoot | null {
  const defaultId = d.default_itinerary.id;
  return defaultId === null
    ? null
    : { contentType: 'demo.itinerary', objectPk: String(defaultId), label: HISTORY_DEFAULT_LABEL };
}

/** The tabs a viewer sees, with Images' count. */
export function itineraryTabs(
  d: Pick<ItineraryDetail, 'usage'>,
  canViewHistory: boolean,
): { id: ItineraryRecordTab; label: string }[] {
  const images = d.usage.image_count;
  return [
    { id: 'itinerary', label: 'Itinerary' },
    { id: 'images', label: images ? `Images ${images.toLocaleString('en-US')}` : 'Images' },
    { id: 'usage', label: 'Usage' },
    ...(canViewHistory ? [{ id: 'history' as const, label: 'History' }] : []),
  ];
}

/** The way back to the grid, in one phrase wherever the page offers it (the load states'
 *  `CatalogLoadError` words it the same). */
const BACK_LABEL = `Back to ${ITINERARY_RECORD.grid}`;

const BACK = (
  <Button component={RouterLink} to={ITINERARY_RECORD.gridHref} startIcon={<ArrowBackIcon />} size="small">
    {BACK_LABEL}
  </Button>
);

export function ItineraryRecordPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`Itinerary #${idStr ?? ''} not found`} action={BACK}>
        That is not an itinerary id.
      </RecordEmptyState>
    );
  }
  const redirect = tab ? ITINERARY_SLUG_REDIRECTS[tab] : undefined;
  if (redirect) {
    return (
      <Navigate
        replace
        to={{
          pathname: `/itineraries/${id}${redirect.tab === 'itinerary' ? '' : `/${redirect.tab}`}`,
          search: location.search,
          hash: redirect.hash ? `#${redirect.hash}` : '',
        }}
      />
    );
  }
  return <ItineraryRecordLoader key={id} id={id} tabParam={tab} />;
}

/** The band's cells (In the app · Trip · App button · Used by · Source). */
const BAND_CELLS = 5;

/** The page's shape while the itinerary loads, so the loaded page lands where the skeleton
 *  stood (Zero-Re-Layout): the header (title, subtitle, the Actions and Edit buttons' places)
 *  and the tabs; on the record tab the fact band (its own chrome and five cells), the cards in
 *  the loaded cards' rows — two columns only where the page will have two — and, from 1100 px
 *  of content, the dock's 380 px column beside them (its Preview | Changes switch for an
 *  operator who will see Edit). The Days card draws `dayRows` rows (null: unknown). Images and
 *  Usage have no band: one panel, with the dock beside it; History one panel alone. `edit`:
 *  the operator will see Edit. */
function ItineraryRecordLoading({
  edit,
  tab,
  dayRows,
}: {
  edit: boolean;
  tab: 'record' | 'docked' | 'history';
  dayRows: number | null;
}) {
  const dock = { width: PREVIEW_DOCK_WIDTH, node: <PreviewDockSkeleton segments={edit} /> };
  return (
    <Box role="status" aria-label="Loading the itinerary" data-itinerary-loading>
      <CatalogHeaderSkeleton edit={edit} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      {tab === 'record' ? (
        <>
          <FactBandSkeleton cells={BAND_CELLS} />
          <RecordPageSkeleton
            layout={itineraryColumns}
            rows={itineraryCardRows}
            card={(section) => itineraryCardSkeleton(section, dayRows)}
            dock={dock}
          />
        </>
      ) : tab === 'docked' ? (
        <RecordPageSkeleton
          layout={itineraryColumns}
          rows={() => []}
          card={() => null}
          lead={<Skeleton variant="rounded" height={320} data-tab-skeleton />}
          dock={dock}
        />
      ) : (
        <Skeleton variant="rounded" height={320} data-tab-skeleton />
      )}
    </Box>
  );
}

function ItineraryRecordLoader({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const qc = useQueryClient();
  const nav = useRecordNav<ItineraryRecordTab>(`/itineraries/${id}`, 'itinerary');
  const detailQ = useItineraryDetail(id, { retry: retryUnlessMissing });
  const detail = detailQ.data;
  if (!detail) {
    const history = hasPerm('console_audit_view') ? ['history'] : [];
    return detailQ.error ? (
      <CatalogLoadError kind={ITINERARY_RECORD} id={id} error={detailQ.error} onRetry={() => void detailQ.refetch()} />
    ) : (
      <ItineraryRecordLoading
        edit={hasPerm(MANAGE_PERM)}
        tab={
          loadsRecordTab(tabParam, ['images', 'usage', ...history])
            ? 'record'
            : tabParam === 'history'
              ? 'history'
              : 'docked'
        }
        dayRows={cachedItineraryDayCount(qc, id)}
      />
    );
  }
  const tabs = itineraryTabs(detail, hasPerm('console_audit_view'));
  const activeTab: ItineraryRecordTab = tabs.find((t) => t.id === tabParam)?.id ?? 'itinerary';
  return (
    <ItineraryRecordLoaded
      detail={detail}
      tabs={tabs}
      activeTab={activeTab}
      nav={nav}
      fetching={detailQ.isFetching}
      refreshError={detailQ.error}
      refetch={() => void detailQ.refetch()}
    />
  );
}

interface LoadedProps {
  detail: ItineraryDetail;
  tabs: { id: ItineraryRecordTab; label: string }[];
  activeTab: ItineraryRecordTab;
  nav: RecordNav<ItineraryRecordTab>;
  fetching: boolean;
  /** A refresh failed: the loaded copy stays under a notice. */
  refreshError: unknown;
  refetch: () => void;
}

/** Why the editor cannot open: it could not load (the catalog records' words). */
export const EDITOR_LOAD_FAILED = CATALOG_EDITOR_LOAD_FAILED;

interface PendingFocus {
  rec: RecordKey;
  path: string;
}

interface EditorProps {
  /** May edit now (the permission, the switch, an operator, the editor loaded). */
  canEdit: boolean;
  editing: boolean;
  /** The permission alone: Edit is offered at all. */
  mayManage: boolean;
  editingDisabled: boolean;
  /** With the permission and the switch on, why Edit is blocked (no operator, the editor
   *  failed to load). */
  editBlockedReason: string | null;
  editContext: ItineraryRecordEditContext | null;
  editorError: unknown;
  retryEditor: () => void;
  pendingFocus: MutableRefObject<PendingFocus | null>;
  /** What the draft holds as stored: edit_context's baselines, the detail's until they arrive. */
  baselines: ItineraryBaselines;
  channel: DaysChannel;
}

/** The records core around the loaded itinerary: the draft (edit_context's baselines, the
 *  detail's until they arrive), the editing session and the page's one preview. */
function ItineraryRecordLoaded(props: LoadedProps) {
  const { detail, nav, activeTab } = props;
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const [params] = useSearchParams();
  const catalogQ = useItineraryEditCatalog();
  const ctxQ = useItineraryEditContext(id);
  const catalog = catalogQ.data ?? null;
  const ctx = ctxQ.data ?? null;
  const ready = catalog !== null && ctx !== null;
  // Only a failure that keeps the editor out: a failed refresh leaves the loaded data in use.
  const editorError = ready ? null : (catalogQ.error ?? ctxQ.error ?? null);

  // What the editor and the page tell the Days card (days brought back, a closed day's field,
  // the saved days' ids).
  const [channel] = useState(() => new DaysChannel());

  // A ledger row for a field that is not on screen: back to the record tab, then focus it
  // once its FieldSlot has mounted; a field of a closed day opens the day first.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: RecordKey, path: string) => {
      if (tabRef.current !== 'itinerary') {
        pendingFocus.current = { rec, path };
        goTab('itinerary');
        return;
      }
      if (parseRecordKey(rec).entity === ITINERARY_DAY_ENTITY) channel.open.emit({ rec, path });
    },
    [goTab, channel],
  );

  const detailBaselines = useMemo(() => itineraryBaselines(detail), [detail]);
  const baselines = ctx?.baselines ?? detailBaselines;
  const config = useItineraryEditorConfig({ detail, catalog, editContext: ctx, onFocusMissing, channel });

  const mayManage = hasPerm(MANAGE_PERM) && (ctx ? ctx.permissions.console_itineraries_manage : true);
  const editingDisabled = itineraryEditBlockedReason({ detail, catalog, editContext: ctx }) === CATALOG_EDITING_OFF;
  const editBlockedReason = !detail.tenant ? CATALOG_NO_OPERATOR : editorError ? EDITOR_LOAD_FAILED : null;
  const canEdit = mayManage && !editingDisabled && editBlockedReason === null;
  const editing = canEdit && ready && params.get('edit') === '1';
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);

  // What a render of the phone applies: what Save would send (O4) — a removed day as its six
  // fields set to null, a new day by its temporary id (`meta.created` names it), the re-key
  // answer with the Custom ID it confirms.
  const wireBaselines = useRef(ctx?.baselines ?? null);
  wireBaselines.current = ctx?.baselines ?? null;
  const previewChangeset = useCallback((body: RecordChangeset): PreviewApplied => {
    const wire = toItineraryChangeset(body, wireBaselines.current);
    return { changes: wire.changes, ...(wire.options ? { options: wire.options } : {}) };
  }, []);

  const editor: EditorProps = {
    canEdit,
    editing,
    mayManage,
    editingDisabled,
    editBlockedReason,
    editContext: ctx,
    editorError,
    retryEditor: () => {
      void catalogQ.refetch();
      void ctxQ.refetch();
    },
    pendingFocus,
    baselines,
    channel,
  };
  return (
    <DraftProvider
      kind="itinerary"
      recordId={id}
      baselines={baselines}
      baselineTs={ctx?.generated_ts ?? detail.updated_ts}
      freshFrom={ITINERARY_FRESH}
    >
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <RecordPreviewProvider
          entity="itinerary"
          id={id}
          canDraft={mayManage && ready && !editingDisabled}
          updatedTs={detail.updated_ts ?? null}
          changeset={previewChangeset}
        >
          <ItineraryRecordShell {...props} {...editor} />
        </RecordPreviewProvider>
      </RecordEditorProvider>
    </DraftProvider>
  );
}

/** Scroll an anchor into view and focus it (a card). */
function reveal(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  el.focus({ preventScroll: true });
}

/** What the number keys reach on the record tab: `1`–`7` the cards, in page order. */
const KEY_TARGETS = ITINERARY_RECORD_SECTIONS.map(itinerarySectionAnchorId);

function ItineraryRecordShell({
  detail,
  tabs,
  activeTab,
  nav,
  fetching,
  refreshError,
  refetch,
  canEdit,
  editing,
  mayManage,
  editingDisabled,
  editBlockedReason,
  editContext,
  editorError,
  retryEditor,
  pendingFocus,
  baselines,
  channel,
}: LoadedProps & EditorProps) {
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const canManage = hasPerm(MANAGE_PERM);
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const copyDeepLink = useCopyDeepLink();
  const { goTab, hrefFor, setParam } = nav;
  const onRecord = activeTab === 'itinerary';
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteRefusal, setDeleteRefusal] = useState<ItineraryDeleteRefusal | null>(null);

  // ---- the editor ------------------------------------------------------------------------
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  // Deleted: the draft went with the itinerary, and the page is leaving.
  const [deleted, setDeleted] = useState(false);
  // Only leaving the itinerary is guarded: its tabs share the draft.
  const { prompt } = useDraftUnsavedGuard(`/itineraries/${id}`, { enabled: !deleted });
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);

  // A ledger row for a field on another tab lands here: focus it once the cards render.
  useEffect(() => {
    if (!onRecord || !pendingFocus.current) return undefined;
    // Taken when the frame runs: an update in between (a preview arriving) re-runs this
    // effect, whose cleanup cancels the frame — the pending focus must survive that.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onRecord, editor, pendingFocus]);

  // ---- the used-by summary: asked for once the band has been on screen ----------------
  const [bandRef, seen] = useSeenOnce<HTMLDivElement>();
  const summaryQ = useItineraryUsedBySummary(id, { enabled: seen });
  const summary = summaryQ.data ?? null;

  // ---- the preview drawer (below 1100 px of content) ------------------------------------
  const narrow = contentWidth > 0 && contentWidth < SHEET_BELOW_PX;
  // The phone is every tab's but History's (the dock's Preview there, the drawer below 1100).
  const hasPreview = activeTab !== 'history';
  const [previewOpen, setPreviewOpen] = useState(false);
  // Closed by the page, not the operator: widening past 1100 px (the dock takes over, and
  // the focus goes to it) or leaving for History.
  const [autoClosed, setAutoClosed] = useState(false);
  useEffect(() => {
    if (previewOpen && (!narrow || !hasPreview)) {
      setAutoClosed(!narrow);
      setPreviewOpen(false);
    }
  }, [narrow, hasPreview, previewOpen]);
  const focusDock = useCallback(() => {
    setAutoClosed(false);
    const aside = document.querySelector<HTMLElement>(`[data-record-page="${id}"] [data-ledger-slot] aside`);
    if (!aside) return;
    if (!aside.hasAttribute('tabindex')) aside.setAttribute('tabindex', '-1');
    aside.focus();
  }, [id]);

  // A card named by the URL (the old Days tab's redirect lands on `#days`).
  const hashCard = HASH_CARDS[location.hash];
  useEffect(() => {
    if (onRecord && hashCard) reveal(itinerarySectionAnchorId(hashCard));
  }, [onRecord, hashCard]);

  // `E` opens the editor; the number keys are the record tab's only (elsewhere — History, …
  // — none is registered). In edit mode the ledger binds ⌘S, ⌘Z / ⇧⌘Z and Esc.
  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onRecord
        ? {
            onStage: (i: number) => {
              const target = KEY_TARGETS[i];
              if (target) reveal(target);
            },
          }
        : {}),
    },
    { stageCount: onRecord ? KEY_TARGETS.length : 0 },
  );

  const tenantId = detail.tenant?.id ?? null;
  const deleteMutation = useMutation({
    mutationFn: () => itinerariesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Itinerary deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['itineraries'] });
      // The draft (and its stored copy) goes with the itinerary; the page leaves once the
      // guard has let go of it (below).
      dispatch({ type: 'RESET' });
      setDeleted(true);
    },
    onError: (e) => {
      // 409 itinerary_in_use / itinerary_placeholder → the refusal dialog says why.
      const refusal = itineraryDeleteRefusal(e);
      if (refusal) {
        setDeleteRefusal(refusal);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  // After the guard's own effects (it is armed above): leaving is no longer blocked.
  useEffect(() => {
    if (deleted) navigate(itinerariesHref({ tenant: tenantId }));
  }, [deleted, navigate, tenantId]);

  const deepLink = detail.branchio_link?.trim() || null;
  const deleteBlocked = itineraryDeleteBlockedReason(detail, summary);
  // O1: view mode's one Published control — the one that applies (a placeholder's header
  // locks it instead). In edit mode `published` is a draft field, the only control.
  const actionBlocked = editingDisabled ? CATALOG_EDITING_OFF : !detail.tenant ? CATALOG_NO_OPERATOR : null;
  // "… now" never runs over unsaved changes: a draft the editor keeps holds it, saying so.
  const publishBlocked = actionBlocked ?? unsavedFirst(count);
  const publishNow = usePublishNow(detail, { blockedReason: publishBlocked });
  // The delete refusal's Unpublish opens Unpublish now's confirmation on the Actions button.
  const askUnpublish = () =>
    publishNow.ask(document.querySelector<HTMLElement>(`[data-record-page="${id}"] [data-page-header] button[aria-haspopup="menu"]`));
  // D6: Regenerate deep link for a manager, link or not yet, in both modes (it leaves the
  // draft alone; a placeholder's header locks it); Regenerate now where the card says one is
  // missing, and Branch.io's progress under the link.
  const regenerate = useRegenerateDeepLink(detail, { canManage, blockedReason: actionBlocked });
  const linkItems: RecordActionItem[] = [
    ...(regenerate.item ? [regenerate.item] : []),
    ...(deepLink ? [{ key: 'copy_deep_link', label: 'Copy deep link', onSelect: () => copyDeepLink(deepLink) }] : []),
  ];
  const actionGroups: RecordActionGroup[] = [
    ...(canManage && !editing ? [{ key: 'visibility', label: 'Visibility', items: [publishNow.item] }] : []),
    { key: 'links', label: 'Links', items: linkItems },
    {
      key: 'investigate',
      label: 'Investigate',
      items: [
        {
          key: 'ext-ids',
          label: 'Ext IDs',
          hint: 'mappings grid',
          onSelect: () => navigate(extIdsHref([cond('model', 'itinerary'), cond('target', String(id))], tenantId)),
        },
        { key: 'usage', label: 'Open Usage', hint: 'every record using it', onSelect: () => goTab('usage') },
        // A real link: cmd-clickable, a new tab without the opener.
        ...(detail.dashboard_url
          ? [{ key: 'dashboard', label: 'Open in dashboard', hint: 'new tab', href: detail.dashboard_url }]
          : []),
      ],
    },
    // Never on a placeholder (P0 refuses it; its header drops Delete as well).
    ...(canManage && !detail.placeholder
      ? [
          {
            key: 'danger',
            label: 'Danger',
            // Disabled up front while what is counted holds it; the way to it beneath.
            items: deleteBlocked
              ? [
                  { key: ITINERARY_ACTION.delete, label: 'Delete…', danger: true, disabled: true, description: deleteBlocked },
                  { key: 'delete-usage', label: 'Open Usage', hint: 'what blocks Delete', onSelect: () => goTab('usage') },
                ]
              : [{ key: ITINERARY_ACTION.delete, label: 'Delete…', danger: true, onSelect: () => setConfirmDelete(true) }],
          },
        ]
      : []),
  ];

  const tabIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);
  const { ask: askRegenerate, watch: deepLinkWatch } = regenerate;
  const display = useMemo<ItineraryDisplayContext>(
    () => ({ detail, links: { tenantId }, deepLink: { regenerate: askRegenerate, watch: deepLinkWatch } }),
    [detail, tenantId, askRegenerate, deepLinkWatch],
  );
  const onCard = useCallback((card: ItineraryCardKey) => reveal(itinerarySectionAnchorId(card)), []);
  const title = itineraryTitle(detail);

  return (
    <Box data-record-page={id}>
      <ItineraryRecordHeader
        detail={detail}
        section={SECTION_LABEL[activeTab]}
        canEdit={mayManage}
        editingDisabled={editingDisabled}
        // No operator: nothing can edit it, the dashboard included (its banner says so).
        editBlockedReason={editBlockedReason}
        editing={editing}
        onEdit={enterEdit}
        onClose={editor.requestClose}
        actionGroups={actionGroups}
        {...(narrow && hasPreview ? { onPreview: () => setPreviewOpen(true), previewOpen } : {})}
      />

      {Boolean(refreshError) && <CatalogRefreshError kind={ITINERARY_RECORD} error={refreshError} onRetry={refetch} />}
      {!detail.tenant && (
        <Alert severity="info" sx={{ mb: 2 }} data-orphan-banner>
          This itinerary has no owning operator, so it can’t be edited.
          {canManage ? ' Delete it from the Actions menu if nothing needs it.' : ''}
        </Alert>
      )}
      {/* The edge states (O9) beside the orphan banner, on every tab: a placeholder's locked
          state holds on Images, Usage and History too. What creates a missing catalog
          itinerary is this page's save for an operator who may edit here, else the dashboard's. */}
      <ItineraryNotices detail={detail} mode={canEdit ? 'edit' : 'view'} gap={2} />
      {Boolean(editorError) && mayManage && detail.tenant && !editingDisabled && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-editor-error
          action={
            <Button color="inherit" size="small" onClick={retryEditor}>
              Retry
            </Button>
          }
        >
          {EDITOR_LOAD_FAILED}, so this itinerary can’t be edited yet.
          {editorError instanceof Error && editorError.message ? ` ${editorError.message}` : ''}
        </Alert>
      )}
      {/* The feed's warnings, in the server's words: an import of its source running now may
          save over an edit (§7). */}
      {editContext && editContext.feed.warnings.length > 0 && (
        <Alert severity="info" icon={<SyncIcon />} sx={{ mb: 2 }} data-feed-warnings>
          {editContext.feed.warnings.join(' ')}
        </Alert>
      )}
      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={enterEdit}>
                  Continue editing
                </Button>
              )}
              <Button color="inherit" size="small" onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}>
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, ITINERARY_TIMEZONE, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={hrefFor} fetching={fetching} {...TABS_LABELS} />

      {onRecord && (
        <Box ref={bandRef} sx={{ mb: 2 }}>
          <ItineraryFactBand
            detail={detail}
            summary={summary}
            tabs={tabIds}
            hrefFor={hrefFor}
            onSelect={onCard}
            notices={false}
          />
        </Box>
      )}

      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          // Every tab but History keeps the column in view mode (the tour record's rule): E
          // changes what it shows, never the layout. History docks the ledger in edit mode only.
          dockInView={hasPreview}
          width={PREVIEW_DOCK_WIDTH}
          stickyTop={DOCK_GAP_PX - SCROLLER_PADDING_PX}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => (
            <PreviewDock
              variant={variant}
              editing={editing}
              canEdit={canEdit}
              preview={hasPreview ? <RecordPreviewPane /> : null}
            />
          )}
        >
          {onRecord && (
            <ItineraryCardsView
              detail={detail}
              baselines={baselines}
              display={display}
              width={contentWidth}
              canEdit={canEdit}
              channel={channel}
            />
          )}
          {/* The gallery is the catalog itinerary's: without one the server has no images to
              list (its 404), so none is asked for, and the tab says what creates it. */}
          {activeTab === 'images' &&
            (detail.default_itinerary.id === null ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} data-images-no-catalog>
                {noCatalogItineraryImagesText(detail, canEdit ? 'edit' : 'view')}
              </Typography>
            ) : (
              <ImageGallery
                api={makeGalleryApi('itineraries', id)}
                queryKey={['itineraries', 'images', id]}
                canManage={canManage}
                showInGroup
              />
            ))}
          {activeTab === 'usage' && <ItineraryUsageTab id={id} />}
          {activeTab === 'history' && (
            <HistoryPanel
              contentType="demo.itinerary_group"
              objectPk={String(id)}
              // O6: the days, facets and pricing file under the catalog (default) itinerary's
              // own root — merged in, each row labelled; no catalog itinerary yet, no `also`.
              also={itineraryHistoryAlso(detail)}
              rootLabel={HISTORY_GROUP_LABEL}
              forbiddenText={HISTORY_FORBIDDEN_TEXT}
              onRestored={() => {
                refetch();
                // A restore moves the stored values: the edit context's baselines follow.
                retryEditor();
              }}
            />
          )}
        </LedgerDock>
      </Box>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated {formatRelativeTime(detail.updated_ts)} ·{' '}
        <RouterLink to={itinerariesHref({ tenant: tenantId })} style={{ color: '#0a1a3f' }}>
          ← {BACK_LABEL}
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this itinerary?"
        message={itineraryDeleteMessage(title, count)}
        confirmLabel="Delete itinerary"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
      <ItineraryDeleteRefusalDialog
        refusal={deleteRefusal}
        name={title}
        published={detail.published}
        {...(canManage && !editing && !publishBlocked ? { onUnpublish: askUnpublish } : {})}
        onClose={() => setDeleteRefusal(null)}
      />
      {publishNow.popover}
      {regenerate.popover}
      {/* Mounted while it may be open or closing — not only while narrow — so a close caused
          by widening still runs its exit, and hands the focus to the dock. */}
      <PreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        restoreFocus={!autoClosed}
        {...(autoClosed ? { onExited: focusDock } : {})}
      >
        <RecordPreviewPane />
      </PreviewDrawer>
      {prompt}
    </Box>
  );
}
